import type { ClientGame, ClientGameCtx } from "./index";
import { legalMoves, owner, type CkMove } from "../../shared/checkers";
import { sound } from "../sound";

export class CheckersClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private boardEl!: HTMLDivElement;
  private statusEl!: HTMLDivElement;
  private squares: HTMLDivElement[] = [];
  private selected = -1;
  private targets: CkMove[] = [];
  private lastHist = 0;

  private get cs() { return this.ctx.room.state.checkers; }
  private get mySide() { return this.ctx.mySlot === this.cs.redSlot ? 0 : 1; }
  private get myTurn() { return this.cs.turnSlot === this.ctx.mySlot; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    sound.unlock();
    const wrap = document.createElement("div");
    wrap.className = "ck-wrap chess-wrap";
    this.statusEl = document.createElement("div");
    this.statusEl.className = "chess-status";
    this.boardEl = document.createElement("div");
    this.boardEl.className = "ck-board";
    const resign = document.createElement("button");
    resign.className = "ghost small";
    resign.textContent = "Resign";
    resign.onclick = () => { if (confirm("Resign this game?")) ctx.room.send("resign"); };
    wrap.append(this.statusEl, this.boardEl, resign);
    ctx.root.appendChild(wrap);
    this.build();
    this.sync();
  }

  unmount() { this.squares = []; }

  private build() {
    this.boardEl.innerHTML = "";
    this.squares = [];
    // red at the bottom for the red player; flip for black
    const flip = this.mySide === 1;
    for (let vr = 0; vr < 8; vr++) {
      for (let vc = 0; vc < 8; vc++) {
        const r = flip ? 7 - vr : vr;
        const c = flip ? 7 - vc : vc;
        const p = r * 8 + c;
        const dark = (r + c) % 2 === 1;
        const cell = document.createElement("div");
        cell.className = `sq ${dark ? "dark" : "light"}`;
        cell.dataset.p = String(p);
        if (dark) cell.onclick = () => this.onCell(p);
        this.boardEl.appendChild(cell);
        this.squares[p] = cell;
      }
    }
  }

  sync() {
    const cs = this.cs;
    if (!cs.board) return;
    const flip = this.mySide === 1;
    const firstP = Number((this.boardEl.firstElementChild as HTMLElement)?.dataset.p ?? -1);
    if ((flip && firstP !== 63) || (!flip && firstP !== 0)) this.build();

    if (cs.history.length > this.lastHist && this.lastHist > 0) sound.place();
    this.lastHist = cs.history.length;

    for (let p = 0; p < 64; p++) {
      const cell = this.squares[p];
      if (!cell) continue;
      const ch = cs.board[p]!;
      const want = ch === "." ? "" : ch;
      const has = cell.querySelector<HTMLElement>(".ck-pc");
      if ((has?.dataset.k || "") !== want) {
        has?.remove();
        if (ch !== ".") {
          const pc = document.createElement("div");
          const side = owner(ch);
          pc.className = `ck-pc ${side === 0 ? "red" : "black"}${ch === "R" || ch === "B" ? " king" : ""}`;
          pc.dataset.k = ch;
          cell.appendChild(pc);
        }
      }
      cell.classList.toggle("last", p === cs.lastFrom || p === cs.lastTo);
      cell.classList.toggle("sel", p === this.selected);
      cell.classList.toggle("target", this.targets.some((t) => t.to === p));
    }

    const phase = this.ctx.room.state.phase;
    if (phase === "results") this.statusEl.textContent = "";
    else this.statusEl.textContent = this.myTurn ? "Your move" : "Opponent thinking…";
    this.statusEl.classList.toggle("mine", this.myTurn && phase === "playing");
    if (phase !== "playing") this.clear();
  }

  private onCell(p: number) {
    if (this.ctx.room.state.phase !== "playing" || !this.myTurn) return;
    const cs = this.cs;

    const chosen = this.targets.find((t) => t.to === p);
    if (this.selected >= 0 && chosen) {
      this.ctx.room.send("move", { from: this.selected, to: p });
      sound.submit();
      this.clear();
      this.sync();
      return;
    }

    if (owner(cs.board[p]!) === this.mySide) {
      const all = legalMoves(cs.board, this.mySide);
      const mine = all.filter((mv) => mv.from === p);
      if (mine.length) {
        this.selected = p;
        this.targets = mine;
        sound.key();
      } else {
        this.clear();
      }
    } else {
      this.clear();
    }
    this.sync();
  }

  private clear() { this.selected = -1; this.targets = []; }

  results(ctx: ClientGameCtx): HTMLElement {
    this.ctx = ctx;
    const cs = ctx.room.state.checkers;
    const reasons: Record<string, string> = {
      "no pieces": "All pieces captured", "no moves": "No legal moves left", resign: "Resignation",
    };
    const wrap = document.createElement("div");
    wrap.className = "ck-wrap chess-wrap results-mini";
    this.statusEl = document.createElement("div");
    this.statusEl.className = "chess-status";
    this.boardEl = document.createElement("div");
    this.boardEl.className = "ck-board";
    wrap.append(this.statusEl, this.boardEl);
    this.selected = -1;
    this.targets = [];
    this.lastHist = cs.history.length;
    this.build();
    this.sync();
    this.statusEl.textContent = reasons[cs.endReason] || "Game over";
    return wrap;
  }
}
